import * as THREE from 'three';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { LineSeg2d } from '@/editor/math/LineSeg2d';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { pixelRatio2d } from '../math/const';
import { EDoorWindowSide } from '../data/model/model';
import { customAlphabet } from 'nanoid';
import { isCircleInPolygon, isCircleIntersectPolygon } from '../math/utils';
import { IPoint, IPoint3d } from '../math/IPoint3d';
import { isPoint3d } from '../math/Point3dUtils';
import { ArcSeg2d } from '../math/arcSeg2d';
import { Point2dUtils } from '../math/Point2dUtils';
import { getBaseShape } from './wallShape';

export const getUniqueId = customAlphabet('abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789', 16);

// TODO: 待优化
export function calArcWallShapePoints(params: {
  start: IPoint2d;
  end: IPoint2d;
  center: IPoint2d;
  clockwise: boolean;
  thickness: number;
}) {
  const { start, end, center, clockwise, thickness } = params;
  const chordVecX = end.x - start.x;
  const chordVecY = end.y - start.y;
  const chordLength = Math.hypot(chordVecX, chordVecY);

  // 弦退化、弦高退化、厚度异常时，按直墙退化处理，避免产生非法面。
  if (chordLength === 0 || !center || thickness <= 0) {
    return getBaseShape({ start, end, thickness }).baseShape;
  }

  const arc = new ArcSeg2d({ start, end, center, clockwise });
  const leftArc = arc.getOffsetArc(thickness / 2);
  const rightArc = arc.getOffsetArc(-thickness / 2);

  return [...leftArc.getPoints(), ...rightArc.getPoints().reverse()];
}

/*
 * 转换鼠标位置 -1 到 1 之间
 * @param event 鼠标事件
 * @param canvasElement
 * @returns 鼠标位置
 */
export function calMousePosition(
  event: Pick<MouseEvent, 'clientX' | 'clientY'>,
  canvasElement: HTMLElement
): THREE.Vector2 {
  const rect = canvasElement.getBoundingClientRect();
  const mouse = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1
  );

  return mouse;
}

export function convertWorldPositionToNdc(worldPosition: IPoint2d, camera: THREE.Camera): THREE.Vector2 {
  const ndc = new THREE.Vector3(worldPosition.x / pixelRatio2d, worldPosition.y / pixelRatio2d, 0);
  ndc.project(camera);
  return new THREE.Vector2(ndc.x, ndc.y);
}

export function mouseVectorToWorldPosition(mouse: IPoint2d, camera: THREE.Camera): IPoint2d {
  const ndc = new THREE.Vector3(mouse.x, mouse.y, 0);
  ndc.unproject(camera);
  return {
    x: ndc.x * pixelRatio2d,
    y: ndc.y * pixelRatio2d
  };
}

/**
 * 将浏览器坐标转换为实际坐标
 * @param point 浏览器坐标点
 * @returns 实际坐标
 */
export function clientPosToWorldPos(point: IPoint2d, camera: THREE.Camera, renderer: THREE.WebGLRenderer): IPoint2d;
export function clientPosToWorldPos(point: IPoint3d, camera: THREE.Camera, renderer: THREE.WebGLRenderer): IPoint3d;
export function clientPosToWorldPos(point: IPoint, camera: THREE.Camera, renderer: THREE.WebGLRenderer): IPoint {
  const canvasRect = renderer.domElement.getBoundingClientRect();
  const canvasLeft = canvasRect.left;
  const canvasTop = canvasRect.top;

  const x = ((point.x - canvasLeft) / canvasRect.width) * 2 - 1;
  const y = (-(point.y - canvasTop) / canvasRect.height) * 2 + 1;
  const z = point.z ?? 0;
  const ndc = new THREE.Vector3(x, y, z);
  ndc.unproject(camera);

  if (isPoint3d(point)) {
    return {
      x: ndc.x * pixelRatio2d,
      y: ndc.y * pixelRatio2d,
      z: ndc.z * pixelRatio2d
    };
  }

  return {
    x: ndc.x * pixelRatio2d,
    y: ndc.y * pixelRatio2d
  };
}

/**
 * 将实际坐标转换为浏览器坐标
 * @param point 场景坐标点
 * @returns 浏览器坐标，如果 point 包含 z 会同时返回包含 z 的坐标，用于 3D 场景
 */
export function worldPosToClientPos(point: IPoint2d, camera: THREE.Camera, renderer: THREE.WebGLRenderer): IPoint2d;
export function worldPosToClientPos(point: IPoint3d, camera: THREE.Camera, renderer: THREE.WebGLRenderer): IPoint3d;
export function worldPosToClientPos(point: IPoint, camera: THREE.Camera, renderer: THREE.WebGLRenderer): IPoint {
  const pointVec = new THREE.Vector3(
    point.x / pixelRatio2d,
    point.y / pixelRatio2d,
    isPoint3d(point) ? point.z / pixelRatio2d : 0
  );
  pointVec.project(camera);
  const rect = renderer.domElement.getBoundingClientRect();
  const x = rect.left + ((pointVec.x + 1) / 2) * rect.width;
  const y = rect.top - ((pointVec.y - 1) / 2) * rect.height;
  if (isPoint3d(point)) {
    // 当 z 轴存在时，说明是在 3D 场景下使用，需要返回 z 轴坐标
    return { x, y, z: pointVec.z };
  }

  return { x, y };
}

/**
 * 将渲染坐标转换为实际坐标
 * @param position 渲染坐标
 * @returns 实际坐标
 */
export function renderPosToWorldPos(position: IPoint2d): IPoint2d;
export function renderPosToWorldPos(position: IPoint3d): IPoint3d;
export function renderPosToWorldPos(position: IPoint): IPoint {
  if (isPoint3d(position)) {
    return {
      x: position.x * pixelRatio2d,
      y: position.y * pixelRatio2d,
      z: position.z * pixelRatio2d
    };
  } else {
    return {
      x: position.x * pixelRatio2d,
      y: position.y * pixelRatio2d
    };
  }
}

/**
 * 将实际坐标转换为渲染坐标
 * @param position 实际坐标
 * @returns 渲染坐标
 */
export function worldPosToRenderPos(position: IPoint2d): IPoint2d;
export function worldPosToRenderPos(position: IPoint3d): IPoint3d;
export function worldPosToRenderPos(position: IPoint): IPoint {
  if (isPoint3d(position)) {
    return {
      x: position.x / pixelRatio2d,
      y: position.y / pixelRatio2d,
      z: position.z / pixelRatio2d
    };
  } else {
    return {
      x: position.x / pixelRatio2d,
      y: position.y / pixelRatio2d
    };
  }
}

// 门窗翻转计算，逆时针方向
export function getNextFlipState(side: EDoorWindowSide, flip: boolean): { side: EDoorWindowSide; flip: boolean } {
  const flipState: { side: EDoorWindowSide; flip: boolean }[] = [
    { side: EDoorWindowSide.right, flip: false },
    { side: EDoorWindowSide.right, flip: true },
    { side: EDoorWindowSide.left, flip: false },
    { side: EDoorWindowSide.left, flip: true }
  ];

  const currentIndex = flipState.findIndex((item) => item.side === side && item.flip === flip);
  const nextIndex = (currentIndex + 1) % flipState.length;

  return flipState[nextIndex];
}

export const isCylinderColumnIntersectPolygon = (
  center: IPoint2d,
  radius: number,
  polygon: IPoint2d[],
  holes: IPoint2d[][] = []
) => {
  let result: 'full' | 'part' | null = null;
  let isIntersectHole = false;

  for (const hole of holes) {
    const hasIntersect = isCircleIntersectPolygon(center, radius, hole);
    if (hasIntersect) {
      isIntersectHole = true;
      break;
    }
  }

  // 圆形是否完全属于房间
  if (!isIntersectHole && isCircleInPolygon(center, radius, polygon)) {
    result = 'full';
    return result;
  }

  // 圆心是否位于区域内
  if (Curve2dUtils.isPointInPolygon(center, polygon)) {
    result = 'part';
  } else {
    const isIntersect = isCircleIntersectPolygon(center, radius, polygon);
    result = isIntersect ? 'part' : null;
  }

  return result;
};

export function isPolygonInOrOnPolygon(innerPolygon: IPoint2d[], outerPolygon: IPoint2d[]) {
  if (innerPolygon.length < 3 || outerPolygon.length < 3) return false;

  return innerPolygon.every((point) => isPointInOrOnPolygon(point, outerPolygon));
}

export function isPolygonIntersectPolygon(polygonA: IPoint2d[], polygonB: IPoint2d[]) {
  if (polygonA.length < 3 || polygonB.length < 3) return false;

  if (polygonA.some((point) => isPointInOrOnPolygon(point, polygonB))) return true;
  if (polygonB.some((point) => isPointInOrOnPolygon(point, polygonA))) return true;

  const polygonASegs = getPolygonSegs(polygonA);
  const polygonBSegs = getPolygonSegs(polygonB);

  return polygonASegs.some((segA) => polygonBSegs.some((segB) => isSegIntersectSeg(segA, segB)));
}

function isPointInOrOnPolygon(point: IPoint2d, polygon: IPoint2d[]) {
  if (polygon.length < 3) return false;

  return Curve2dUtils.isPointOnPolygon(point, polygon) || Curve2dUtils.isPointInPolygon(point, polygon);
}

function getPolygonSegs(polygon: IPoint2d[]) {
  return polygon.map((point, index) => new LineSeg2d(point, polygon[(index + 1) % polygon.length]));
}

function isSegIntersectSeg(segA: LineSeg2d, segB: LineSeg2d) {
  return Boolean(segA.intersection(segB) || (segA.isCollinear(segB) && segA.getOverlapSegment(segB)));
}

export const getIncrementId = (() => {
  let count = 1;
  return () => count++;
})();

export function getBoxOutline(params: { center: IPoint2d; width: number; thickness: number; rotation: number }) {
  const { center, width, thickness, rotation } = params;
  const outline: { start: IPoint2d; end: IPoint2d }[] = [];

  let leftTopPoint = { x: -width / 2 + center.x, y: -thickness / 2 + center.y };
  let rightTopPoint = { x: width / 2 + center.x, y: -thickness / 2 + center.y };
  let rightBottomPoint = { x: width / 2 + center.x, y: thickness / 2 + center.y };
  let leftBottomPoint = { x: -width / 2 + center.x, y: thickness / 2 + center.y };

  leftTopPoint = Point2dUtils.rotatePoint(leftTopPoint, center, rotation);
  rightTopPoint = Point2dUtils.rotatePoint(rightTopPoint, center, rotation);
  rightBottomPoint = Point2dUtils.rotatePoint(rightBottomPoint, center, rotation);
  leftBottomPoint = Point2dUtils.rotatePoint(leftBottomPoint, center, rotation);

  outline.push({ start: leftTopPoint, end: rightTopPoint });
  outline.push({ start: rightTopPoint, end: rightBottomPoint });
  outline.push({ start: rightBottomPoint, end: leftBottomPoint });
  outline.push({ start: leftBottomPoint, end: leftTopPoint });

  return outline;
}
